import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useMemo } from "react";
import { AuthContext } from "../AuthProvider";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useDashboardInstanceSlug } from "@/hooks/useDashboardNavigation";
import { useOrgFromPathname } from "@/hooks/useOrgFromPathname";
import { SIDEBAR_AUTOMATION_ROWS_STORAGE_KEY_PREFIX } from "@/components/codesmith/useCachedSidebarAutomationRows";
import { namedOrgKey, orgStorageKey, type NamedOrg } from "@/lib/named-org";

// Codesmith access is per org, so the cache is keyed by org to avoid
// leaking one org's flag into another while orgs load.
const CODESMITH_ENABLED_STORAGE_KEY_PREFIX = "sidebar-codesmith-enabled";
// Routes without an org segment (/usage, /settings, /support, /admin, ...)
// have no org to key the lookup with while auth and orgs load, so the last
// resolved org is kept as a pointer for those lookups. The server-side
// active org is what those routes resolve to, and this is the best local
// guess of it.
const LAST_ACTIVE_ORG_STORAGE_KEY = "sidebar-codesmith-last-org";
// The cache belongs to one account: without this marker, a later session on
// the same browser could adopt the previous account's flag on org-less
// routes via the last-org pointer until orgs resolve.
const CACHE_OWNER_STORAGE_KEY = "sidebar-codesmith-user";
function storageKeyForOrg(org: NamedOrg) {
  return orgStorageKey(`${CODESMITH_ENABLED_STORAGE_KEY_PREFIX}:`, org);
}

// The pointer is a `namedOrgKey` (`"ghe/acme"`); neither slug nor org name
// contains "/". Hand-parsed rather than zod so the sidebar chunk stays small.
function readLastActiveOrg(): NamedOrg | undefined {
  const stored = window.localStorage.getItem(LAST_ACTIVE_ORG_STORAGE_KEY);
  const slash = stored?.indexOf("/") ?? -1;
  if (!stored || slash <= 0 || slash === stored.length - 1) return undefined;
  return {
    instanceSlug: stored.slice(0, slash),
    orgName: stored.slice(slash + 1)
  };
}
export function clearCodesmithLayoutCache() {
  if (typeof window === "undefined") return;
  try {
    const doomed = [LAST_ACTIVE_ORG_STORAGE_KEY, CACHE_OWNER_STORAGE_KEY];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(`${CODESMITH_ENABLED_STORAGE_KEY_PREFIX}:`) ||
      // Automation groups are per user, so the next account must not
      // reserve this one's section.
      key?.startsWith(SIDEBAR_AUTOMATION_ROWS_STORAGE_KEY_PREFIX)) {
        doomed.push(key);
      }
    }
    for (const key of doomed) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Local storage is an optimization; the resolved org setting remains authoritative.
  }
}

/**
 * Last resolved active org, for navigation targets on org-less routes while
 * orgs load. Paired with `getCachedCodesmithEnabled`: when the cached flag
 * mounts Codesmith UI before `activeNamedOrg` resolves, this is the org that
 * cached flag belongs to, and org-less paths like `/sessions/new` don't exist
 * (sessions live under `/[org]/sessions`). The orgs query resolves the
 * active org of the instance the URL addresses, so a pointer cached under
 * another instance is no guess of it and is skipped.
 */
export function getCachedLastActiveOrg(instanceSlug: string): NamedOrg | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const org = readLastActiveOrg();
    return org?.instanceSlug === instanceSlug ? org : undefined;
  } catch {
    return undefined;
  }
}
export function getCachedCodesmithEnabled(org: NamedOrg | null | undefined): boolean | undefined {
  if (typeof window === "undefined" || !org) return undefined;
  try {
    const value = window.localStorage.getItem(storageKeyForOrg(org));
    return value === null ? undefined : value === "true";
  } catch {
    return undefined;
  }
}

/**
 * Codesmith availability for the active org, falling back to the cached
 * last-known value while the org query is in flight so Codesmith-gated UI
 * (the rail row) doesn't pop in after every load.
 * `undefined` means genuinely unknown (first visit).
 */
export function useCodesmithEnabledWithCache() {
  const $ = _c(16);
  const {
    user
  } = useContext(AuthContext);
  const {
    activeNamedOrg,
    codesmithEnabled,
    codesmithEnabledKnown,
    orgsResolved
  } = useActiveGithubOrgForUserNonSuspense();
  const urlOrg = useOrgFromPathname();
  const urlInstanceSlug = useDashboardInstanceSlug(user?.default_instance_slug);
  let t0;
  if ($[0] !== activeNamedOrg || $[1] !== urlInstanceSlug || $[2] !== urlOrg) {
    t0 = activeNamedOrg ?? (urlOrg ? {
      orgName: urlOrg,
      instanceSlug: urlInstanceSlug
    } : null);
    $[0] = activeNamedOrg;
    $[1] = urlInstanceSlug;
    $[2] = urlOrg;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const org = t0;
  const isUnresolved = !orgsResolved;
  let t1;
  let t2;
  if ($[4] !== codesmithEnabled || $[5] !== codesmithEnabledKnown || $[6] !== isUnresolved || $[7] !== org || $[8] !== user) {
    t1 = () => {
      if (!user) {
        return;
      }
      try {
        if (window.localStorage.getItem(CACHE_OWNER_STORAGE_KEY) !== String(user.id)) {
          clearCodesmithLayoutCache();
          window.localStorage.setItem(CACHE_OWNER_STORAGE_KEY, String(user.id));
        }
        if (isUnresolved) {
          return;
        }
        if (!org) {
          window.localStorage.removeItem(LAST_ACTIVE_ORG_STORAGE_KEY);
          return;
        }
        if (!codesmithEnabledKnown) {
          window.localStorage.removeItem(storageKeyForOrg(org));
          if (namedOrgKey(readLastActiveOrg()) === namedOrgKey(org)) {
            window.localStorage.removeItem(LAST_ACTIVE_ORG_STORAGE_KEY);
          }
          return;
        }
        window.localStorage.setItem(storageKeyForOrg(org), String(codesmithEnabled));
        window.localStorage.setItem(LAST_ACTIVE_ORG_STORAGE_KEY, namedOrgKey(org));
      } catch {}
    };
    t2 = [user, isUnresolved, org, codesmithEnabled, codesmithEnabledKnown];
    $[4] = codesmithEnabled;
    $[5] = codesmithEnabledKnown;
    $[6] = isUnresolved;
    $[7] = org;
    $[8] = user;
    $[9] = t1;
    $[10] = t2;
  } else {
    t1 = $[9];
    t2 = $[10];
  }
  useEffect(t1, t2);
  let t3;
  if ($[11] !== codesmithEnabled || $[12] !== isUnresolved || $[13] !== org || $[14] !== urlInstanceSlug) {
    t3 = isUnresolved ? getCachedCodesmithEnabled(org ?? getCachedLastActiveOrg(urlInstanceSlug)) : codesmithEnabled;
    $[11] = codesmithEnabled;
    $[12] = isUnresolved;
    $[13] = org;
    $[14] = urlInstanceSlug;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}